import { arrayOf, object, func, bool } from 'prop-types';
import { useMemo } from 'react';
import { useFlexLayout, useSortBy, useTable } from 'react-table';
import DropDownIcon from './../../../images/svg/dropdown.svg';
import { Box, Flex, WithIcon } from './layouts';
import {useTranslation} from "react-i18next";

const headerProps = (props, { column }) => getStyles(props, column.align);

const cellProps = (props, { cell }) => getStyles(props, cell.column.align);

const getStyles = (props, align = 'left') => [
  props,
  {
    style: {
      justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
      alignItems: 'center',
      display: 'flex',
    },
  },
];

const Table = ({
  columns,
  data,
  initialState,
  handleRowClick,
  resetCursor,
  resetCursorCondition,
}) => {
  const { t } = useTranslation();
  const defaultColumn = useMemo(
    // width is used for both the flex-basis and flex-grow
    () => ({ width: 100 }),
    []
  );
  // Use the state and functions returned from useTable to build your UI
  const {
    getTableProps,
    getTableBodyProps,
    headers,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState,
    },
    useFlexLayout,
    useSortBy
  );

  const reset = (row) => {
    if (resetCursorCondition) {
      return resetCursorCondition(row);
    } else {
      return resetCursor;
    }
  };

  return (
    <>
      {headers.some((header) => typeof header.Header === 'string') && (
        <div style={{ textTransform: 'uppercase' }}>
          <Flex {...getTableProps()} fontScale="xs" height="16px">
            {headers.map((column, i) => (
              <Box
                textAlign="left"
                key={i}
                fontWeight="bold"
                {...column.getHeaderProps({
                  ...column.getSortByToggleProps({ title: t('Sorteren op {{header}}', {header: column.Header})}),
                  ...headerProps,
                })}
              >
                <WithIcon dir="rtl" color="secondaryFont">
                  {column.isSorted ? (
                    <DropDownIcon
                      display="block"
                      width="10px"
                      height="10px"
                      transform={`rotate(${column.isSortedDesc ? 0 : 180})`}
                    />
                  ) : (
                    ''
                  )}
                  <span
                    css={{ ':hover': { textDecoration: 'underline #A0B3BA' } }}
                  >
                    {column.render('Header')}
                  </span>
                </WithIcon>
              </Box>
            ))}
          </Flex>
        </div>
      )}

      <div {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <Box
              key={i}
              {...row.getRowProps()}
              css={({ fontWeights }) => ({
                fontWeight: row.original.isBold && fontWeights.bold,
                cursor: handleRowClick && !reset(row) ? 'pointer' : 'default',
                svg: { display: 'block' }
              })}
              pb="m"
              pt="m"
              alignItems="center"
              fontScale="s"
              borderBottom="default"
              onClick={handleRowClick ? () => handleRowClick(row) : null}
            >
              {row.cells.map((cell, i) => (
                <Box key={i} {...cell.getCellProps(cellProps)}>
                  {cell.render('Cell')}
                </Box>
              ))}
            </Box>
          );
        })}
      </div>
    </>
  );
};

Table.propTypes = {
  columns: arrayOf(object).isRequired,
  data: arrayOf(object).isRequired,
  initialState: object,
  handleRowClick: func,
  resetCursor: bool,
  resetCursorCondition: func,
};

Table.defaultProps = {
  initialState: {},
  resetCursor: false,
};

export default Table;
